import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { map } from 'rxjs/operators';
import { createHttpParams } from '~/infrastructure/http/params';
import { augmentPaging } from '~/infrastructure/paging';
import { IAssetDto, IAssetListing } from '~/models/modules/Asset/asset';
import { AssetListMode } from '~/models/modules/Asset/Transactions/CheckOut/assetListMode';
import { ApiServiceBase, API_NAMES } from '~/services/api';

@Injectable({
  providedIn: 'root'
})
export class AssetService extends ApiServiceBase {
  constructor(injector: Injector, private http: HttpClient) {
    super(injector);
    this.setBase(API_NAMES.assets);
  }

  getAssetGrid(paramsObj?: any, model?: any, modelTypeId?: number, currentAssetId?: number, noParent?: number, _checkedOut?: number, searchPage?: string, screenSiteId?: number) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    paramsObj.modelTypeId = modelTypeId;
    paramsObj.currentAssetId = currentAssetId;
    paramsObj.noParent = noParent;
    paramsObj.checkedOut = _checkedOut;
    paramsObj.searchPage = searchPage ?? 'assets';
    paramsObj.screenSiteId = screenSiteId;

    let params = createHttpParams(paramsObj);

    return this.http.post<any[]>(this._url(`/grid-data`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  getAssetWithoutResevedGrid(paramsObj?: any, model?: any, modelTypeId?: number, currentAssetId?: number, noParent?: number, _checkedOut?: number, searchPage?: string, screenSiteId?: number) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    paramsObj.modelTypeId = modelTypeId;
    paramsObj.currentAssetId = currentAssetId;
    paramsObj.noParent = noParent;
    paramsObj.checkedOut = _checkedOut;
    paramsObj.searchPage = searchPage ?? 'assets';
    paramsObj.screenSiteId = screenSiteId;

    let params = createHttpParams(paramsObj);

    return this.http.post<any[]>(this._url(`/grid-data-without-reserved`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  getAssetEnterpriseInvitoryGrid(paramsObj?: any, model?: any) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    let params = createHttpParams(paramsObj);

    return this.http.post<any[]>(this._url(`/grid-inv-enterprise`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  getAssetEnterpriseHistoryGrid(paramsObj?: any, model?: any) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    let params = createHttpParams(paramsObj);

    return this.http.post<any[]>(this._url(`/grid-history-enterprise`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  getAssetGridByMode(paramsObj?: any, _listMode?: AssetListMode, model?: any, screenSiteId?: number) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    paramsObj.listMode = _listMode;
    paramsObj.screenSiteId = screenSiteId;
    let params = createHttpParams(paramsObj);

    return this.http.post<any[]>(this._url(`/grid-data-mode`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  getAssetList(paramsObj?: any, _checkOutOnly?: number, _custodyOnly?: number, _checkedOut?: number, _checkedOutTrans?: number, _noCheckedOutTrans?: number, screenSiteId?: number) {
    paramsObj = paramsObj || {};
    paramsObj.checkOutOnly = _checkOutOnly; //available for work asset check out
    paramsObj.custodyOnly = _custodyOnly; //available to work asset custody
    paramsObj.checkedOut = _checkedOut; //include checked out to be available to be checked out again
    paramsObj.checkedOutTrans = _checkedOutTrans; //gets only the checked out assets by check out transaction
    paramsObj.noCheckedOutTrans = _noCheckedOutTrans; //gets only those not checked out by a transaction
    paramsObj.screenSiteId = screenSiteId;

    let params = createHttpParams(paramsObj);

    return this.client.get<IAssetListing[]>(this._url(`/list-data`), { params, observe: 'response' });
  }

  getAssetDropDownListData(listMode: AssetListMode, paramsObj?: any, screenSiteId?: number) {
    paramsObj = paramsObj || {};
    paramsObj.screenSiteId = screenSiteId;
    let params = createHttpParams(paramsObj);

    return this.client.post<IAssetListing[]>(this._url(`/droplist-data`), listMode, { params, observe: 'response' });
  }

  getTransferCommonList(paramsObj?: any, fromSiteId?: number, toSiteId?: number) {
    paramsObj = paramsObj || {};
    let params = createHttpParams(paramsObj);

    return this.client.get<IAssetDto>(this._url(`/transfer-list-enterprise/${fromSiteId}/${toSiteId}`), { params, observe: 'response' })
  }

  getModelAssets(modelId: number) {
    return this.client.get<IAssetDto>(this.url(`/Assets/get-model-assets/${modelId}`));
  }

  getLocationAssets(locationId: number) {
    return this.client.get<IAssetDto[]>(this._url(`/get-location-assets/${locationId}`));
  }

  getPersonAssets(personId: number) {
    return this.client.get<IAssetDto[]>(this._url(`/get-person-assets/${personId}`));
  }

  getAssetById(id: number) {
    return this.client.get<IAssetDto>(this._url(`/${id}`))
  }

  getAssetRange(ids: number[]) {
    return this.client.post<IAssetDto[]>(this._url(`/range`), ids);
  }

  createAsset(model: any) {
    return this.client.post<any>(this._url(), model);
  }

  updateAsset(id: number, model: any) {
    return this.client.post<any>(this._url(`/${id}`), model);
  }

  deactivateAsset(obj: any) {
    return this.client.put(this._url(`/deactivate`), obj);
  }

  deactivateAssetBatch(obj: any) {
    return this.client.put(this._url(`/batchDeactivate`), obj);
  }

  ValidateAssetForDeactivation(obj: any) {
    return this.client.put<any>(this._url(`/validateBatchDeactivate`), obj);
  }

  reactivateAsset(id: number) {
    return this.client.post<any>(this._url(`/reactivate/${id}`), null);
  }

  reactivateAssetBatch(obj: any) {
    return this.client.put(this._url(`/batchReactivate`), obj);
  }

  getAssetPermissions(paramsObj?: any, model?: any) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    let params = createHttpParams(paramsObj);

    return this.http.post<any[]>(this._url(`/getAssetPermissions`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  getStockPermissions(paramsObj?: any, model?: any) {
    paramsObj = paramsObj || {};
    paramsObj.view = 'summary';
    let params = createHttpParams(paramsObj);

    return this.http.post<any[]>(this._url(`/getStockPermissions`), model, { params, observe: 'response' }).pipe(map(augmentPaging));
  }

  private _url(v?: string | undefined) {
    v = v || '';
    return this.url(`/Assets${v}`);
  }
}
